import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CategoryProduct } from "@/lib/catalog";

interface UseSimilarProductsResult {
  products: CategoryProduct[];
  loading: boolean;
  error: string | null;
}

/**
 * Recomendador perpetuo de anuncios similares.
 * Estrategia (en cascada) para que NUNCA deje de sugerir:
 *   1. Anuncios activos de la MISMA categoría, ordenados por más vistos y recientes.
 *   2. Si no alcanzan el límite, completa con otros anuncios activos recientes.
 * Siempre excluye el anuncio actual y evita duplicados.
 */
export default function useSimilarProducts(
  currentId: number | null,
  category: string | null,
  limit = 8,
): UseSimilarProductsResult {
  const [products, setProducts] = useState<CategoryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSimilar = useCallback(async () => {
    if (currentId == null) {
      setProducts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const collected: CategoryProduct[] = [];
      const seen = new Set<number>([currentId]);

      const pushRows = (rows: CategoryProduct[] | null) => {
        (rows ?? []).forEach((row) => {
          if (row == null || seen.has(row.id)) return;
          seen.add(row.id);
          collected.push(row);
        });
      };

      // 1) Misma categoría primero.
      if (category) {
        const { data, error: err } = await supabase
          .from("products")
          .select("*, profiles(full_name)")
          .eq("status", "active")
          .eq("category", category)
          .neq("id", currentId)
          .order("views", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(limit);
        if (err) throw err;
        pushRows(data as unknown as CategoryProduct[]);
      }

      // 2) Respaldo: otros anuncios activos recientes para llenar el resto.
      if (collected.length < limit) {
        const { data, error: err } = await supabase
          .from("products")
          .select("*, profiles(full_name)")
          .eq("status", "active")
          .neq("id", currentId)
          .order("created_at", { ascending: false })
          .limit(limit * 3);
        if (err) throw err;
        pushRows(data as unknown as CategoryProduct[]);
      }

      setProducts(collected.slice(0, limit));
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "No se pudieron cargar los anuncios similares.",
      );
    } finally {
      setLoading(false);
    }
  }, [currentId, category, limit]);

  useEffect(() => {
    fetchSimilar();
  }, [fetchSimilar]);

  return { products, loading, error };
}